import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Employee } from '@qaroni-app/employee/types/employee';
import { EmployeeApiObject } from '@qaroni-app/employee/types/employee-response';
import { CommonsHttpService } from '@qaroni-core/services/commons/commons-http/commons-http.service';
import { QaroniIntermediate } from '@qaroni-core/types/qaroni-intermediate/qaroni-intermediate';
import { Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { DepartmentsSnackbarsService } from '../../departments/services/departments-snackbars.service';
import {
  ChecklistsActivities,
  ChecklistsActivitiesApiResponse,
  CheckListsActivitiesCreateDto,
  ChecklistsActivitiesResponse,
  ChecklistsActivitiesUpdateDto,
} from '../types/checklists-activities';
import { ChecklistsActivitiesHttpService } from './checklists-activities-http.service';

@Injectable({
  providedIn: 'root',
})
export class ChecklistsActivitiesService extends QaroniIntermediate {
  private activitiesSubject = new Subject<ChecklistsActivities[]>();
  private activitySubject = new Subject<ChecklistsActivities | null>();
  private employeesActivitiesSubject = new Subject<Employee[]>();

  private http = inject(ChecklistsActivitiesHttpService);
  private commonHttp = inject(CommonsHttpService);
  private snackbarService = inject(DepartmentsSnackbarsService);

  public getActivitiesSubject$() {
    return this.activitiesSubject.asObservable().pipe(shareReplay(1));
  }

  public list(companyID: number, checklistID: number, params?: Params) {
    this.http.list$(companyID, checklistID, params).subscribe({
      next: this.nextList,
      error: this.errorList,
    });
  }

  private nextList = (response: ChecklistsActivitiesResponse) => {
    if (!response.result) {
      this.activitiesSubject.next([]);
      return;
    }

    this.activitiesSubject.next(response.result);
    this.linksSubject.next(response.links);
  };

  private errorList = () => {
    this.activitiesSubject.next([]);
  };

  public getActivitySubject$() {
    return this.activitySubject.asObservable().pipe(shareReplay(1));
  }

  public get(companyID: number, checklistID: number, activityID: number) {
    this.http.get$(companyID, checklistID, activityID).subscribe({
      next: this.nextGet,
      error: this.errorGet,
    });
  }

  private nextGet = (response: ChecklistsActivitiesResponse) => {
    if (!response.result) {
      this.activitySubject.next(null);
      return;
    }

    this.activitySubject.next(response.result[0]);
  };

  private errorGet = () => {
    this.activitySubject.next(null);
  };

  public create(
    companyID: number,
    checklistID: number,
    activity: CheckListsActivitiesCreateDto
  ) {
    this.http.create$(companyID, checklistID, activity).subscribe({
      next: this.nextActivity,
      error: this.errorActivity,
    });
  }

  private nextActivity = (response: ChecklistsActivitiesResponse) => {
    if (!response.result) {
      this.activitySubject.next(null);
      return;
    }

    this.snackbarService.successAddActivity();
    this.activitySubject.next(response.result[0]);
  };

  private errorActivity = () => {
    this.activitySubject.next(null);
  };

  public update(
    companyID: number,
    checklistID: number,
    activityID: number,
    activity: ChecklistsActivitiesUpdateDto
  ) {
    this.http.update$(companyID, checklistID, activityID, activity).subscribe({
      next: this.nextUpdateActivity,
      error: this.errorUpdateActivity,
    });
  }

  private nextUpdateActivity = (response: ChecklistsActivitiesResponse) => {
    if (!response.result) {
      this.activitySubject.next(null);
      return;
    }

    this.snackbarService.successUpdateActivity();
    this.activitySubject.next(response.result[0]);
  };

  private errorUpdateActivity = () => {
    this.activitySubject.next(null);
  };

  public delete(companyID: number, checklistID: number, activityID: number) {
    this.http.delete$(companyID, checklistID, activityID).subscribe({
      next: this.nextDeleteActivity,
      error: this.errorDeleteActivity,
    });
  }

  private nextDeleteActivity = (response: ChecklistsActivitiesApiResponse) => {
    if (!response) {
      this.messageSubject.next('');
      return;
    }

    if (this.commonHttp.validationsHttp.verifyStatus204(response)) {
      this.snackbarService.successDeleteActivity();
      this.messageSubject.next('delete Ok');
    }
  };

  private errorDeleteActivity = () => {
    this.messageSubject.next('');
  };

  public getEmployeeActivitiesSubject$() {
    return this.employeesActivitiesSubject.asObservable().pipe(shareReplay(1));
  }

  public getEmployeeActivities(
    companyID: number,
    checklistID: number,
    activityID: number,
    params?: Params
  ) {
    this.http
      .getEmployeeActivities$(companyID, checklistID, activityID, params)
      .subscribe({
        next: this.nextEmployeeList,
        error: this.errorEmployeeList,
      });
  }

  private nextEmployeeList = (response: EmployeeApiObject) => {
    if (!response.result) {
      this.employeesActivitiesSubject.next([]);
      return;
    }

    this.employeesActivitiesSubject.next(response.result);
    this.linksSubject.next(response.links);
  };

  private errorEmployeeList = () => {
    this.employeesActivitiesSubject.next([]);
  };

  public deleteFile(
    companyID: number,
    checklistID: number,
    activityID: number,
    fileID: number
  ) {
    this.http
      .deleteFile$(companyID, checklistID, activityID, fileID)
      .subscribe({
        next: this.nextDeleteFile,
        error: this.errorDeleteFile,
      });
  }

  private nextDeleteFile = (response: ChecklistsActivitiesApiResponse) => {
    if (!response) {
      this.messageSubject.next('');
      return;
    }

    if (this.commonHttp.validationsHttp.verifyStatus204(response)) {
      this.messageSubject.next('delete Ok');
    }
  };

  private errorDeleteFile = () => {
    this.messageSubject.next('');
  };
}
